<template>
  <!-- ── Icon mode ─────────────────────────────────────────────────────── -->
  <template v-if="mode === 'icon'">
    <div
      class="device-icon-pin"
      :class="sizeClass"
      :style="{ '--pin-active-color': 'var(--device-color-switch)' }"
      :title="device.deviceName">
      <!-- The drawn eye inside the status ring (P14): presence fills the
           family's green ring, an empty room keeps the track. -->
      <ControlStatusRing :value="present ? 100 : 0">
        <ControlIcon name="eye" :size="pinIconSize" :style="present ? { color: 'var(--device-color-switch)' } : {}" />
      </ControlStatusRing>
    </div>
  </template>

  <!-- ── Grid mode ─────────────────────────────────────────────────────── -->
  <template v-else-if="mode === 'grid'">
    <DeviceGridCell
      :icon="device.icon || 'sensor_occupied'"
      :name="displayName"
      :is-on="present"
      active-color="green-400"
      @toggle="emit('open-control-modal')"
      @long-press="emit('open-control-modal')" />
  </template>

  <!-- ── Control / full mode ───────────────────────────────────────────── -->
  <!-- No head of its own: the sheet's chrome paints the circle, the name and the
       presence pill — the body starts at the readings. -->
  <template v-else>
    <div class="presence-sheet">
      <!-- A fall is an incident, not a state: it gets the loudest row on the card -->
      <div v-if="fallDetected" class="presence-fall">
        <ControlIcon name="bell" size="1.15em" />
        {{ tt('Fall detected', 'Zaznan padec') }}
      </div>

      <!-- Activity: what the sensor's auto sit / work / sleep detection reports -->
      <div class="presence-section">
        <div class="presence-label">{{ tt('Activity', 'Aktivnost') }}</div>
        <div class="presence-chips">
          <span
            v-for="activity in ACTIVITIES"
            :key="activity.value"
            class="presence-chip"
            :class="{ 'presence-chip--on': activity.value === activityValue }">
            {{ activityWordOf(activity) }}
          </span>
        </div>
      </div>

      <!-- Zones: one row per detection zone, occupied ones lit -->
      <div v-if="zones.length" class="presence-section">
        <div class="presence-label">{{ tt('Zones', 'Cone') }}</div>
        <div class="presence-zones" :style="{ gridTemplateColumns: `repeat(${zoneColumns}, minmax(0, 1fr))` }">
          <div
            v-for="zone in zones"
            :key="zone.id"
            class="presence-zone"
            :class="{ 'presence-zone--occupied': zone.occupied }">
            <span class="presence-zone-dot"></span>
            <span class="presence-zone-name">{{ zoneLabel(zone) }}</span>
          </div>
        </div>
      </div>

      <!-- Radar / light readouts -->
      <div class="presence-kv">
        <ControlValueTile icon="person" :label="tt('Targets', 'Osebe')" :value="targetsText" />
        <ControlValueTile icon="pin" :label="tt('Distance', 'Razdalja')" :value="distanceText" />
        <ControlValueTile icon="warm" :label="tt('Light', 'Svetloba')" :value="illuminanceText" />
      </div>

      <!-- The class's one control: detection sensitivity -->
      <div v-if="sensitivityId" class="presence-section">
        <div class="presence-label">{{ tt('Sensitivity', 'Občutljivost') }}</div>
        <div class="presence-chips">
          <ControlChip
            v-for="option in SENSITIVITIES"
            :key="option.value"
            :disabled="controlling"
            :active="option.value === sensitivityValue"
            @click="setSensitivity(option.value)">
            {{ tt(option.label, option.sl) }}
          </ControlChip>
        </div>
      </div>
    </div>
  </template>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue';
import ControlChip from '../device-sheet/ControlChip.vue';
import ControlIcon from '../device-icons/ControlIcon.vue';
import ControlStatusRing from '../device-sheet/ControlStatusRing.vue';
import ControlValueTile from '../device-sheet/ControlValueTile.vue';
import DeviceGridCell from '../panel/DeviceGridCell.vue';
import { useDeviceValues } from '../../composables/useDeviceValues';
import { useDeviceControl } from '../../composables/useDeviceControl';
import { tt } from '../../utils/consoleText';
import type { Device, DeviceValueWithReferences } from '@/types';

interface Props {
  device: Device;
  mode?: 'icon' | 'control' | 'grid';
  size?: 'sm' | 'md' | 'lg';
  fluid?: boolean;
  displayMode?: 'full' | 'room';
}

const props = withDefaults(defineProps<Props>(), { mode: 'icon', size: 'md' });

const emit = defineEmits<{
  toggle: [];
  'open-control-modal': [];
  'view-stream': [];
  'view-details': [];
  'value-update': [valueId: string, newValue: unknown];
}>();

/** The class's activity enum (`PresenceSensor`) with this build's own words. */
const ACTIVITIES: Array<{ value: number; label: string; sl: string }> = [
  { value: 0, label: 'Clear', sl: 'Prosto' },
  { value: 1, label: 'Moving', sl: 'Gibanje' },
  { value: 2, label: 'Still', sl: 'Miruje' },
  { value: 3, label: 'Sitting', sl: 'Sedi' },
  { value: 4, label: 'Working', sl: 'Dela' },
  { value: 5, label: 'Sleeping', sl: 'Spi' },
];

/** The class's sensitivity enum (`PresenceSensor`, the one settable value). */
const SENSITIVITIES: Array<{ value: number; label: string; sl: string }> = [
  { value: 0, label: 'Low', sl: 'Nizka' },
  { value: 1, label: 'Medium', sl: 'Srednja' },
  { value: 2, label: 'High', sl: 'Visoka' },
];

const ZONE_KEY = /^zone_(\d+)_presence$/;

// ── State ────────────────────────────────────────────────────────────────
const { currentValues, initializeValues } = useDeviceValues(props.device.id);
const { controlling, setProperty } = useDeviceControl();

if (props.device.deviceValues?.length) initializeValues(props.device.deviceValues);
watch(
  () => props.device.deviceValues,
  (v) => {
    if (v?.length) initializeValues(v);
  },
  { deep: true },
);

function readValue(key: string): unknown {
  if (key in currentValues.value) return currentValues.value[key];
  return props.device.deviceValues?.find((value) => value.valueKey === key)?.currentValue;
}

function toBoolean(raw: unknown): boolean {
  if (raw === true || raw === 1) return true;
  const text = String(raw ?? '')
    .trim()
    .toLowerCase();
  return text === 'true' || text === 'on' || text === '1';
}

const present = computed<boolean>(() => toBoolean(readValue('presence')));
const fallDetected = computed<boolean>(() => toBoolean(readValue('fall_detected')));

/** The activity enum value, resolved through the hub's value or its label. */
const activityValue = computed<number>(() => {
  const raw = readValue('activity');
  const numeric = Number(raw);
  if (Number.isFinite(numeric) && numeric >= 0 && numeric <= 5) return numeric;
  const text = String(raw ?? '')
    .trim()
    .toLowerCase();
  const byLabel = ACTIVITIES.find((activity) => activity.label.toLowerCase() === text);
  return byLabel ? byLabel.value : 0;
});
const activityWordOf = (activity: { label: string; sl: string }): string => tt(activity.label, activity.sl);

const sensitivityValue = computed<number>(() => {
  const numeric = Number(readValue('sensitivity'));
  return Number.isFinite(numeric) ? numeric : 1;
});
const sensitivityId = computed<string | null>(() => {
  const row = props.device.deviceValues?.find((value) => value.valueKey === 'sensitivity');
  return row ? String(row.id) : null;
});

function numberOf(key: string): number | null {
  const raw = readValue(key);
  if (raw === undefined || raw === null || raw === '') return null;
  const numeric = Number(raw);
  return Number.isFinite(numeric) ? numeric : null;
}

const targetsText = computed<string>(() => {
  const targets = numberOf('target_count');
  return targets === null ? '--' : String(Math.round(targets));
});
const distanceText = computed<string>(() => {
  const distance = numberOf('distance');
  return distance === null ? '--' : `${distance.toFixed(1)} m`;
});
const illuminanceText = computed<string>(() => {
  const illuminance = numberOf('illuminance');
  return illuminance === null ? '--' : `${Math.round(illuminance)} lx`;
});

/** The device's own zone rows, in index order, each with its live occupancy. */
const zones = computed<Array<{ id: string; index: number; name: string; occupied: boolean }>>(() => {
  const rows = (props.device.deviceValues ?? []) as DeviceValueWithReferences[];
  return rows
    .map((row) => ({ row, match: ZONE_KEY.exec(String(row.valueKey ?? '').toLowerCase()) }))
    .filter((entry): entry is { row: DeviceValueWithReferences; match: RegExpExecArray } => entry.match !== null)
    .map(({ row, match }) => ({
      id: String(row.id),
      index: Number(match[1]),
      name: String(row.name ?? `Zone ${match[1]}`),
      occupied: toBoolean(currentValues.value[row.valueKey ?? ''] ?? row.currentValue),
    }))
    .sort((a, b) => a.index - b.index);
});

const zoneCount = computed<number>(() => zones.value.length);
const zoneColumns = computed<number>(() => Math.min(4, Math.max(1, zoneCount.value)));

/** "Zone 3" reads as "Cona 3" on a Slovene screen; a custom label passes through. */
function zoneLabel(zone: { index: number; name: string }): string {
  const match = /^Zone (\d+)$/.exec(zone.name);
  return match ? tt(`Zone ${match[1]}`, `Cona ${match[1]}`) : zone.name;
}

const displayName = computed(() =>
  props.displayMode === 'full' ? props.device.deviceName : props.device.shortName || props.device.deviceName,
);
const sizeClass = computed(() => (props.fluid ? 'device-icon-pin--fluid' : `device-icon-pin--${props.size ?? 'md'}`));

/** The drawn eye's size inside the pin (P14), in the `GenericIcon` steps. */
const pinIconSize = computed(() => {
  if (props.fluid) return '28px';
  switch (props.size) {
    case 'lg':
      return '28px';
    case 'sm':
      return '16px';
    default:
      return '22px';
  }
});

async function setSensitivity(value: number) {
  const ok = await setProperty(props.device, 'sensitivity', value);
  if (ok && sensitivityId.value) emit('value-update', sensitivityId.value, value);
}
</script>

<style scoped>
/* ── Control mode: the sheet body ────────────────────────────────────────
   The chrome owns the head (circle, name, presence pill); the body is the
   rows — activity chips, one row per detection zone, the radar readouts on
   the shared value tiles, the sensitivity chips and the fall incident. The
   tokens are the `device-sheet` primitives' own recipes. */
.presence-sheet {
  display: flex;
  flex-direction: column;
  gap: calc(0.875rem * var(--ui-space-factor));
}

.presence-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.presence-label {
  font-size: 0.6875rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: rgb(var(--gray-500));
}

.dark .presence-label {
  color: rgb(var(--gray-400));
}

.presence-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

/* The activity row is read-only — it shows what the sensor detected, so it is a
   chip rather than a `ControlChip` button. The recipe is the chip's own. */
.presence-chip {
  display: inline-flex;
  align-items: center;
  padding: calc(0.375rem * var(--ui-space-factor)) calc(0.75rem * var(--ui-space-factor));
  border: 1px solid var(--glass-edge);
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.1);
  font-size: 0.7813rem;
  font-weight: 600;
  line-height: 1.2;
  color: rgb(var(--gray-600));
}

.dark .presence-chip {
  background: rgba(255, 255, 255, 0.03);
  color: rgb(var(--gray-300));
}

.presence-chip--on {
  background: color-mix(in srgb, var(--sheet-accent, var(--device-color-light)) 16%, transparent);
  border-color: color-mix(in srgb, var(--sheet-accent, var(--device-color-light)) 55%, transparent);
  color: var(--sheet-accent, var(--device-color-light));
}

.presence-zones {
  display: grid;
  gap: 0.5rem;
}

.presence-zone {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  min-width: 0;
  padding: calc(0.5rem * var(--ui-space-factor)) calc(0.625rem * var(--ui-space-factor));
  border: 1px solid var(--glass-edge);
  border-radius: calc(0.75rem * var(--ui-radius-factor));
  background: rgba(148, 163, 184, 0.08);
  font-size: 0.75rem;
  font-weight: 600;
  color: rgb(var(--gray-500));
}

.dark .presence-zone {
  background: rgba(255, 255, 255, 0.03);
  color: rgb(var(--gray-400));
}

.presence-zone-dot {
  width: 0.5rem;
  height: 0.5rem;
  flex-shrink: 0;
  border-radius: 50%;
  background: rgba(148, 163, 184, 0.55);
}

.presence-zone-name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* An occupied zone is the one thing on this sheet that lights up. */
.presence-zone--occupied {
  background: color-mix(in srgb, rgb(34, 197, 94) 16%, transparent);
  border-color: color-mix(in srgb, rgb(34, 197, 94) 45%, transparent);
  color: rgb(21, 128, 61);
}

.dark .presence-zone--occupied {
  background: color-mix(in srgb, rgb(34, 197, 94) 18%, transparent);
  color: rgb(134, 239, 172);
}

.presence-zone--occupied .presence-zone-dot {
  background: rgb(34, 197, 94);
}

.presence-kv {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
}

/* A fall is an incident, not a state: the loudest row on the sheet. */
.presence-fall {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: calc(0.625rem * var(--ui-space-factor)) calc(0.75rem * var(--ui-space-factor));
  border: 1px solid rgba(220, 38, 38, 0.35);
  border-radius: calc(0.75rem * var(--ui-radius-factor));
  background: rgba(220, 38, 38, 0.08);
  font-size: 0.875rem;
  font-weight: 600;
  color: rgb(220, 38, 38);
}

.dark .presence-fall {
  border-color: rgba(248, 113, 113, 0.35);
  background: rgba(248, 113, 113, 0.1);
  color: rgb(248, 113, 113);
}
</style>
